import React from "react";
import { css } from "@linaria/core";
import { styled } from "@linaria/react";
import { colors } from "../Theme";
import Text from "../Text";
import cn from "classnames";
import { locale } from "../i18n";

const DISABLED = "disabled";
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 100px;
  &.disabled {
    opacity: 0.5;
  }
`;

const PercentBarWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 8px;
  width: 100%;
`;

const progressBar = css`
  flex: 7;
  min-width: 100px;
  height: 8px;
  background-color: ${colors.titanium100};
  border-radius: 4px;
  overflow: hidden;
`;

const Percent = styled(Text)`
  flex: 1;
  min-width: 40px;
  text-align: right;
  padding-left: 8px;
`;

const Filler = styled.div<{
  color?: string | undefined;
}>`
  transform: scaleX(calc(var(--progress) * 1));
  transition: transform 0.3s ease-in-out;
  transform-origin: left center;
  height: 100%;
  background-color: ${props => props.color ?? colors.carbon600};
`;

const Title = styled(Text)`
  color: ${colors.carbon800};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Description = styled(Text)`
  color: ${colors.carbon500};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

interface PercentBarProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "color"> {
  percent: number;
  title?: string;
  description?: string;
  color?: string;
  showPercent?: boolean;
  disabled?: boolean;
}

const PercentBar = ({
  percent,
  title,
  description,
  color,
  showPercent = true,
  disabled = false,
  className,
}: PercentBarProps) => {
  const clampedMaxPercent = Math.min(1, percent);
  const clampedPercent = Math.max(0, clampedMaxPercent);
  const percentage = new Intl.NumberFormat(locale, {
    style: "percent",
    maximumSignificantDigits: 4,
  }).format(percent);

  return (
    <Wrapper
      className={cn(className, {
        [DISABLED]: disabled,
      })}
    >
      <Title type="body2SemiBold">{title}</Title>
      <PercentBarWrapper>
        <div className={progressBar}>
          <Filler
            color={color ?? colors.carbon600}
            style={{ "--progress": clampedPercent } as React.CSSProperties}
          />
        </div>
        {showPercent && <Percent type="body2SemiBold">{percentage}</Percent>}
      </PercentBarWrapper>
      <Description type="body3SemiBold">{description}</Description>
    </Wrapper>
  );
};

export default PercentBar;
